import { enums } from "@fraction/shared";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import type { VelocityImportJob } from "../api/FractionClient";
import { useFractionClient } from "./FractionClientContext";

const TERMINAL_STATUSES: enums.AsyncJobStatus[] = [
  enums.AsyncJobStatus.DONE,
  enums.AsyncJobStatus.FAILED,
  enums.AsyncJobStatus.CANCELLED,
];

const isTerminal = (status?: enums.AsyncJobStatus) => !!status && TERMINAL_STATUSES.includes(status);

const velocityDealsKey = (userId?: string, statuses?: number[]) =>
  ["velocity-deals", userId, statuses?.join(",") ?? "default"] as const;
const velocityImportJobsKey = (userId?: string) => ["velocity-import-jobs", userId] as const;
const velocityImportJobKey = (jobId?: string) => ["velocity-import-job", jobId] as const;

/**
 * The broker's Velocity book with per-deal `alreadyImported` state, one Newton page at
 * a time.
 *
 * Newton's page size is fixed at 100 and it offers no free-text search, so `statuses` is
 * the only real latency lever: narrowing the statuses narrows how many 100-deal
 * round-trips exist at all. Omit it for the live pipeline (New + Lead). Each page is one
 * backend→Newton round-trip (~1-2s), so render `deals` as soon as page 1 lands and pull
 * the rest with `fetchNextPage`. Kept disabled until the consuming surface is open.
 */
export function useVelocityDeals({
  enabled = true,
  userId,
  statuses,
}: {
  enabled?: boolean;
  userId?: string;
  statuses?: number[];
} = {}) {
  const client = useFractionClient();
  const query = useInfiniteQuery({
    queryKey: velocityDealsKey(userId, statuses),
    enabled,
    initialPageParam: 1,
    queryFn: ({ pageParam }) => client.listVelocityDeals({ page: pageParam, statuses }),
    getNextPageParam: (lastPage) => (lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined),
    staleTime: 60_000,
    retry: false,
  });

  const deals = useMemo(() => (query.data?.pages ?? []).flatMap((page) => page.deals), [query.data?.pages]);
  const totalDeals = query.data?.pages?.[0]?.totalDeals ?? 0;

  return { ...query, deals, totalDeals };
}

/**
 * Exact loan-code lookup — the only search Newton supports server-side. Finds a deal by
 * VDLAB id even when it sits outside the statuses currently being browsed and hasn't
 * been paged in yet. Disabled unless `loanCode` is set.
 */
export function useVelocityDealLookup({ loanCode, userId }: { loanCode?: string; userId?: string } = {}) {
  const client = useFractionClient();
  return useQuery({
    queryKey: ["velocity-deal-lookup", userId, loanCode] as const,
    enabled: !!loanCode,
    queryFn: async () => (await client.listVelocityDeals({ loanCode })).deals,
    staleTime: 60_000,
    retry: false,
  });
}

/** The per-user webhook URL the broker pastes into Velocity's settings. */
export function useVelocityWebhookUrl({
  enabled = true,
  userId,
}: {
  enabled?: boolean;
  userId?: string;
} = {}) {
  const client = useFractionClient();
  return useQuery({
    queryKey: ["velocity-webhook-url", userId] as const,
    enabled,
    queryFn: async () => (await client.getVelocityWebhookUrl()).url,
    staleTime: Number.POSITIVE_INFINITY,
  });
}

/**
 * When the most recent webhook-sourced application arrived for this broker
 * (`null` if none yet). Drives the "Webhook status" line in Settings.
 */
export function useVelocityWebhookStatus({
  enabled = true,
  userId,
}: {
  enabled?: boolean;
  userId?: string;
} = {}) {
  const client = useFractionClient();
  return useQuery({
    queryKey: ["velocity-webhook-status", userId] as const,
    enabled,
    queryFn: () => client.getVelocityWebhookStatus(),
    staleTime: 60_000,
  });
}

/** Recent import jobs (newest first), for resuming progress display after a reload. */
export function useVelocityImportJobs({
  enabled = true,
  userId,
}: {
  enabled?: boolean;
  userId?: string;
} = {}) {
  const client = useFractionClient();
  return useQuery({
    queryKey: velocityImportJobsKey(userId),
    enabled,
    queryFn: async () => (await client.listVelocityImportJobs()).jobs,
  });
}

/**
 * Start an async deal import and follow its progress. `startImport` kicks the job
 * off ({ loanCodes } or { all: true }); `job` polls every 2s until terminal, then
 * the deals list + recent jobs are invalidated so `alreadyImported` flags refresh.
 * Pass `jobId` to resume following an existing job instead of starting a new one.
 */
export function useVelocityDealImport({
  userId,
  jobId: initialJobId,
}: {
  userId?: string;
  jobId?: string;
} = {}) {
  const client = useFractionClient();
  const queryClient = useQueryClient();
  const [jobId, setJobId] = useState<string | undefined>(initialJobId);

  const mutation = useMutation({
    mutationFn: (args: { loanCodes: string[] } | { all: true }) => client.importVelocityDeals(args),
    onSuccess: (job) => {
      queryClient.setQueryData(velocityImportJobKey(job.id), job);
      setJobId(job.id);
    },
  });

  const jobQuery = useQuery({
    queryKey: velocityImportJobKey(jobId),
    enabled: !!jobId,
    queryFn: () => client.getVelocityImportJob(jobId as string),
    refetchInterval: (query) =>
      isTerminal((query.state.data as VelocityImportJob | undefined)?.status) ? false : 2_000,
  });

  const job = jobQuery.data;
  const finished = isTerminal(job?.status);

  useEffect(() => {
    if (finished) {
      queryClient.invalidateQueries({ queryKey: velocityDealsKey(userId) });
      queryClient.invalidateQueries({ queryKey: velocityImportJobsKey(userId) });
    }
  }, [finished, queryClient, userId]);

  return {
    startImport: mutation.mutateAsync,
    isStarting: mutation.isPending,
    startError: mutation.error,
    job,
    isImporting: mutation.isPending || (!!jobId && !finished),
    finished,
    /** Stop following the current job (e.g. after the user dismisses the progress UI). */
    reset: () => {
      setJobId(undefined);
      mutation.reset();
    },
  };
}
